import { Button } from 'primereact/button'
import React, { useRef, useState, useEffect } from 'react'
import ImageCropper from 'Components/UIComponent/ImageCropper'

export default function AttachmentPreview({ attachments = [], scrollToBottom, handleMediaSelected, onAddAttachment, removeAttachment, onUpdateAttachment, onCloseAll, onSendAttachments, isUploadingImage, setAttachments, addFiles: addFilesProp }) {
  const fileInputRef = useRef(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [showCropper, setShowCropper] = useState(false)
  const [croppingIndex, setCroppingIndex] = useState(null)
  const [isAnnotating, setIsAnnotating] = useState(false)
  const [brushColor, setBrushColor] = useState('#00a884') // WhatsApp green
  const [brushSize, setBrushSize] = useState(4)
  const [strokesById, setStrokesById] = useState(() => new Map()) // { [attachmentId]: { strokes: [...], redo: [...] } }
  const canvasRef = useRef(null)
  const imgElementRef = useRef(null)
  const isDrawingRef = useRef(false)
  const lastPointRef = useRef(null)
  const scaleRef = useRef({ x: 1, y: 1 })
  const brushColorRef = useRef('#00a884')
  const brushSizeRef = useRef(4)

  const activeIdRef = useRef(null)
  const isAnnotatingRef = useRef(false)

  const getActiveStrokeState = (id = activeIdRef.current) => strokesById.get(id) || { strokes: [], redo: [] }
  const getActiveStrokes = () => getActiveStrokeState().strokes || []
  const getActiveRedo = () => getActiveStrokeState().redo || []

  // local captions map used as fallback if parent doesn't provide onUpdateAttachment
  const [localCaptions, setLocalCaptions] = useState(() => new Map())

  // active attachment shortcuts (placed early to avoid temporal dead zone)
  const active = attachments[activeIndex] || null
  const isImage = active?.mime?.startsWith?.('image/')
  const isVideo = active?.mime?.startsWith?.('video/')

  // keep activeIndex valid when attachments change

  useEffect(() => {
    if (attachments.length === 0) {
      setActiveIndex(0)
      setIsAnnotating(false)
    } else if (activeIndex >= attachments.length) {
      setActiveIndex(attachments.length - 1)
    }

    // keep localCaptions entries in sync for new attachments
    setLocalCaptions((prev) => {
      const next = new Map()

      attachments.forEach((att) => {
        next.set(att.id, att.caption ?? prev.get(att.id) ?? '')
      })

      return next
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments])

  useEffect(() => {
    activeIdRef.current = active?.id || null
  }, [active?.id])

  useEffect(() => {
    isAnnotatingRef.current = isAnnotating
  }, [isAnnotating])

  useEffect(() => {
    activeIdRef.current = active?.id || null
  }, [active?.id])

  useEffect(() => {
    isAnnotatingRef.current = isAnnotating
  }, [isAnnotating])

  const COLORS = ['#00a884', '#111b21', '#f44336', '#ff9800', '#2196f3', '#9c27b0']

  const resetCanvas = (img) => {
    const canvas = canvasRef.current

    if (!canvas || !img) return

    canvas.width = img.naturalWidth
    canvas.height = img.naturalHeight

    // scale factors for pointer coordinate mapping
    const rect = canvas.getBoundingClientRect()

    scaleRef.current = {
      x: canvas.width / rect.width,
      y: canvas.height / rect.height
    }

    const ctx = canvas.getContext('2d')

    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

    getActiveStrokes().forEach((stroke) => drawStroke(ctx, stroke))
  }

  const drawStroke = (ctx, stroke) => {
    if (!stroke?.points?.length) return

    ctx.save()
    ctx.lineWidth = stroke.size
    ctx.strokeStyle = stroke.color
    ctx.lineJoin = 'round'
    ctx.lineCap = 'round'
    ctx.beginPath()
    ctx.moveTo(stroke.points[0].x, stroke.points[0].y)
    for (let i = 1; i < stroke.points.length; i += 1) {
      const p = stroke.points[i]

      ctx.lineTo(p.x, p.y)
    }
    ctx.stroke()
    ctx.restore()
  }

  const redrawCanvas = () => {
    const canvas = canvasRef.current
    const img = imgElementRef.current

    if (!canvas || !img) return
    const ctx = canvas.getContext('2d')

    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    getActiveStrokes().forEach((stroke) => drawStroke(ctx, stroke))
  }

  const redrawWithStrokes = (strokes) => {
    const canvas = canvasRef.current
    const img = imgElementRef.current

    if (!canvas || !img) return
    const ctx = canvas.getContext('2d')

    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    ;(strokes || []).forEach((stroke) => drawStroke(ctx, stroke))
  }

  const pointerToCanvas = (event) => {
    const canvas = canvasRef.current

    if (!canvas) return null
    const rect = canvas.getBoundingClientRect()
    const x = (event.clientX - rect.left) * scaleRef.current.x
    const y = (event.clientY - rect.top) * scaleRef.current.y

    return { x, y }
  }

  const handleMouseDown = (e) => {
    if (!isAnnotatingRef.current) return
    const point = pointerToCanvas(e)

    if (!point) return

    isDrawingRef.current = true
    lastPointRef.current = point

    const stroke = {
      id: `stroke-${Date.now()}`,
      color: brushColorRef.current,
      size: brushSizeRef.current,
      points: [point]
    }

    setStrokesById((prev) => {
      const map = new Map(prev)
      const key = activeIdRef.current

      if (!key) return prev
      const existing = map.get(key) || { strokes: [], redo: [] }

      map.set(key, { ...existing, strokes: [...existing.strokes, stroke], redo: [] })

      return map
    })
  }

  const handleMouseMove = (e) => {
    if (!isAnnotatingRef.current || !isDrawingRef.current) return
    const point = pointerToCanvas(e)

    if (!point || !lastPointRef.current) return

    // append to latest stroke
    setStrokesById((prev) => {
      const map = new Map(prev)
      const key = activeIdRef.current

      if (!key) return prev
      const existing = map.get(key) || { strokes: [], redo: [] }

      if (!existing.strokes.length) return prev

      const updatedStrokes = [...existing.strokes]
      const currentStroke = { ...updatedStrokes[updatedStrokes.length - 1] }

      currentStroke.points = [...currentStroke.points, point]
      updatedStrokes[updatedStrokes.length - 1] = currentStroke
      map.set(key, { ...existing, strokes: updatedStrokes })

      return map
    })

    // draw incremental segment for smoothness
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')

    if (ctx) {
      ctx.save()
      ctx.lineWidth = brushSizeRef.current
      ctx.strokeStyle = brushColorRef.current
      ctx.lineJoin = 'round'
      ctx.lineCap = 'round'
      ctx.beginPath()
      ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y)
      ctx.lineTo(point.x, point.y)
      ctx.stroke()
      ctx.restore()
    }

    lastPointRef.current = point
  }

  const handleMouseUp = () => {
    if (!isAnnotatingRef.current) return
    isDrawingRef.current = false
    lastPointRef.current = null
  }

  useEffect(() => {
    if (!isAnnotating) return undefined
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isAnnotating, handleMouseMove])

  useEffect(() => {
    // exit annotation/crop when switching attachments
    setIsAnnotating(false)
    setShowCropper(false)
  }, [activeIndex])

  // Keep refs in sync with state for event handlers
  useEffect(() => {
    brushColorRef.current = brushColor
  }, [brushColor])

  useEffect(() => {
    brushSizeRef.current = brushSize
  }, [brushSize])

  // Initialize canvas whenever we enter annotation or active image changes
  useEffect(() => {
    if (!isAnnotating || !isImage) return
    const img = new Image()

    img.src = active.previewUrl
    img.onload = () => {
      imgElementRef.current = img
      resetCanvas(img)
    }
  }, [isAnnotating, active?.id, active?.previewUrl, isImage])

  const startAnnotating = () => {
    if (!isImage) return
    setShowCropper(false)
    setIsAnnotating(true)
  }

  const stopAnnotating = () => {
    setIsAnnotating(false)
    isDrawingRef.current = false
    lastPointRef.current = null
  }

  const clearActiveAnnotations = () => {
    // remove strokes for the current attachment
    setStrokesById((prev) => {
      const key = activeIdRef.current

      if (!key || !prev.has(key)) return prev
      const next = new Map(prev)

      next.delete(key)

      return next
    })

    // redraw base image only (remove any drawn strokes from canvas preview)
    if (imgElementRef.current && canvasRef.current) {
      const img = imgElementRef.current

      resetCanvas(img)
    }
  }

  const handleCancelAnnotate = () => {
    clearActiveAnnotations()
    stopAnnotating()
  }

  // Undo/Redo temporarily disabled for current PR. Handlers are kept commented for reference.
  // const handleUndo = () => {
  //   let nextStrokes = null
  //   setStrokesById((prev) => {
  //     const map = new Map(prev)
  //     const key = activeIdRef.current
  //     if (!key) return prev
  //     const existing = map.get(key)
  //     if (!existing?.strokes?.length) return prev
  //     const updated = existing.strokes.slice(0, -1)
  //     const redoStack = [existing.strokes[existing.strokes.length - 1], ...(existing.redo || [])]
  //     nextStrokes = updated
  //     map.set(key, { ...existing, strokes: updated, redo: redoStack })
  //     return map
  //   })
  //   requestAnimationFrame(() => (nextStrokes ? redrawWithStrokes(nextStrokes) : redrawCanvas()))
  // }
  //
  // const handleRedo = () => {
  //   let nextStrokes = null
  //   setStrokesById((prev) => {
  //     const map = new Map(prev)
  //     const key = activeIdRef.current
  //     if (!key) return prev
  //     const existing = map.get(key)
  //     if (!existing?.redo?.length) return prev
  //     const [nextStroke, ...restRedo] = existing.redo
  //     const updatedStrokes = [...(existing.strokes || []), nextStroke]
  //     nextStrokes = updatedStrokes
  //     map.set(key, { ...existing, strokes: updatedStrokes, redo: restRedo })
  //     return map
  //   })
  //   requestAnimationFrame(() => (nextStrokes ? redrawWithStrokes(nextStrokes) : redrawCanvas()))
  // }

  const handleApplyAnnotation = () => {
    if (!isImage || !canvasRef.current || !imgElementRef.current) {
      stopAnnotating()

      return
    }

    const offscreen = document.createElement('canvas')

    offscreen.width = canvasRef.current.width
    offscreen.height = canvasRef.current.height
    const ctx = offscreen.getContext('2d')

    ctx.drawImage(imgElementRef.current, 0, 0, offscreen.width, offscreen.height)
    getActiveStrokes().forEach((stroke) => drawStroke(ctx, stroke))

    offscreen.toBlob(
      (blob) => {
        if (!blob) {
          stopAnnotating()

          return
        }
        const ext = active?.name?.split('.').pop() || 'png'
        const baseName = active?.name?.replace(/\.[^.]+$/, '') || 'annotated'
        const file = new File([blob], `${baseName}_edited.${ext}`, { type: blob.type || 'image/png' })
        const newPreviewUrl = URL.createObjectURL(blob)

        setAttachments((prev) => {
          const next = [...prev]

          next[activeIndex] = {
            ...active,
            file,
            previewUrl: newPreviewUrl,
            name: file.name
          }

          return next
        })

        // revoke old preview to avoid leaks
        if (active?.previewUrl) URL.revokeObjectURL(active.previewUrl)
        stopAnnotating()
      },
      'image/png',
      0.95
    )
  }

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || [])

    if (files.length && onAddAttachment) onAddAttachment(files)
    // reset so same file can be reselected later
    e.target.value = ''

    // If parent provided attachments prop, calculate new index
    if (attachments) {
      const newIndex = attachments.length + files.length - 1

      setActiveIndex(newIndex)
      handleMediaSelected(files)
    }
  }

  const handleRemoveAttachment = (id) => {
    if (removeAttachment) removeAttachment(id)
    // adjust active index if necessary (will also be clamped in useEffect)
    if (attachments.length === 1) {
      setActiveIndex(0)
    } else {
      const idx = attachments.findIndex((a) => a.id === id)

      if (idx !== -1 && idx <= activeIndex && activeIndex > 0) {
        setActiveIndex((i) => Math.max(0, i - 1))
      }
    }
  }

  const handleCloseAll = () => {
    if (onCloseAll) return onCloseAll()
    // fallback: call removeAttachment for each
    attachments.forEach((a) => removeAttachment && removeAttachment(a.id))

    if (scrollToBottom) {
      setTimeout(() => {
        scrollToBottom()
      }, 0)
    }
  }

  const updateCaptionLocal = (id, newCaption) => {
    // call parent updater if provided

    if (onUpdateAttachment) {
      onUpdateAttachment(id, { caption: newCaption })
    } else {
      // local fallback
      setLocalCaptions((prev) => {
        const next = new Map(prev)

        next.set(id, newCaption)

        return next
      })
    }
  }

  const getCaption = (id) => {
    if (onUpdateAttachment) {
      const att = attachments.find((a) => a.id === id)

      return att?.caption ?? ''
    }

    return localCaptions.get(id) ?? ''
  }

  const handleSend = () => {
    if (onSendAttachments) return onSendAttachments()
    // fallback: log attachments and captions
    console.warn(
      'onSendAttachments prop not provided. attachments:',
      attachments.map((a) => ({ id: a.id, caption: getCaption(a.id) }))
    )
  }

  const handleCropClick = () => {
    if (isImage) {
      setCroppingIndex(activeIndex)
      setShowCropper(true)
    }
  }

  const handleCropComplete = (croppedBlob) => {
    if (croppingIndex === null) return

    const attachment = attachments[croppingIndex]

    if (!attachment) return

    // Create new file from blob
    const croppedFile = new File([croppedBlob], attachment.name, { type: attachment.mime || 'image/png' })

    // Create new preview URL
    const newPreviewUrl = URL.createObjectURL(croppedBlob)

    // Revoke old preview URL
    if (attachment.previewUrl) {
      URL.revokeObjectURL(attachment.previewUrl)
    }

    // Update attachment in the array
    if (setAttachments) {
      setAttachments((prev) => {
        const updated = [...prev]
        const oldCaption = attachment.caption || ''

        // Replace the old attachment with the cropped one
        updated[croppingIndex] = {
          ...attachment,
          file: croppedFile,
          previewUrl: newPreviewUrl,
          caption: oldCaption,
          size: croppedBlob.size
        }

        return updated
      })
    } else if (addFilesProp) {
      // Fallback: use addFiles if setAttachments is not available
      const newAttachments = addFilesProp([croppedFile], 'media')

      if (newAttachments?.length && removeAttachment) {
        const oldCaption = attachment.caption || ''

        newAttachments[0].caption = oldCaption

        // Remove old and add new
        removeAttachment(attachment.id)
        if (handleMediaSelected) {
          handleMediaSelected([croppedFile])
        }
      }
    }

    setShowCropper(false)
    setCroppingIndex(null)
  }

  const handleCropCancel = () => {
    setShowCropper(false)
    setCroppingIndex(null)
  }

  // If there are no attachments, render minimal composer
  if (!attachments.length) {
    return (
      <div className="wa-compose-container">
        <div className="wa-input-bar">
          <input type="file" ref={fileInputRef} style={{ display: 'none' }} multiple onChange={handleFileSelect} />
          <button type="button" className="wa-input-plus" onClick={() => fileInputRef.current.click()}>
            +
          </button>
          <textarea className="wa-input" placeholder="Type a message..." value={''} onChange={() => {}} rows={1} />
          <button className="wa-send-btn" onClick={handleSend} aria-label="Send">
            📤
          </button>
        </div>
      </div>
    )
  }

  const renderThumb = (att, thumbIsImage, thumbIsVideo) => {
    if (thumbIsImage) return <img src={att.previewUrl} alt={att.name} />
    if (thumbIsVideo) return <video src={att.previewUrl} muted />

    return <div className="wa-doc-thumb">📄</div>
  }

  const renderBigPreview = () => {
    // Show cropper if active
    if (showCropper && isImage) {
      return <ImageCropper imageSrc={active.previewUrl} onCrop={handleCropComplete} onCancel={handleCropCancel} />
    }

    if (isAnnotating && isImage) {
      return (
        <div className="wa-annotator">
          <canvas
            ref={canvasRef}
            className="wa-annotator-canvas"
            onMouseDown={handleMouseDown}
            // pointer events handled globally for move/up
          />
        </div>
      )
    }

    if (isImage) {
      return <img src={active.previewUrl} alt={active?.name} className="wa-big-media" />
    }

    if (isVideo) {
      return <video src={active.previewUrl} className="wa-big-media" controls playsInline muted />
    }

    return (
      <div className="wa-big-doc">
        <div className="wa-doc-icon">📄</div>
        <div className="wa-doc-name">{active?.name}</div>
      </div>
    )
  }

  return (
    <div className="wa-compose-container wa-whatsapp-style">
      {/* big centered preview */}
      <div className="wa-big-preview-wrap">
        <div className="wa-big-preview">
          {/* {isImage ? (
            <img src={active.previewUrl} alt={active?.name} className="wa-big-media" />
          ) : isVideo ? (
            <video src={active.previewUrl} className="wa-big-media" controls playsInline muted />
          ) : (
            <div className="wa-big-doc">
              <div className="wa-doc-icon">📄</div>
              <div className="wa-doc-name">{active?.name}</div>
            </div>
          )} */}
          {renderBigPreview()}
        </div>

        {/* Commented for current PR */}
        {/* {isAnnotating && isImage && (
          <div className="wa-preview-actions-left">
            <Button className="wa-action-btn" icon="pi pi-undo" onClick={handleUndo} disabled={!getActiveStrokes().length} aria-label="Undo annotation" />
            <Button className="wa-action-btn redo-btn" icon="pi pi-undo" onClick={handleRedo} disabled={!getActiveRedo().length} aria-label="Redo annotation" />
          </div>
        )} */}

        <div className="wa-preview-actions">
          {!showCropper && isImage && !isAnnotating && (
            <button className="wa-crop-btn" onClick={handleCropClick} aria-label="Crop image">
              <svg className="crop-rotate" xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#fff">
                <path d="M480 0q-94 0-177.5-33.5t-148-93Q90-186 49-266.5T0-440h80q8 72 38.5 134.5t79 110.5Q246-147 309-117.5T444-82l-62-62 56-56L618-20Q584-9 549.5-4.5T480 0Zm120-200v-80H360q-33 0-56.5-23.5T280-360v-240h-80v-80h80v-80h80v400h400v80h-80v80h-80Zm0-240v-160H440v-80h160q33 0 56.5 23.5T680-600v160h-80Zm280-80q-7-72-38-134.5T762.5-765Q714-813 651-842.5T516-878l62 62-56 56-180-180q34-11 68.5-15.5T480-960q94 0 177.5 33.5t148 93Q870-774 911-693.5T960-520h-80Z" />
              </svg>
            </button>
          )}
          {isImage && !isAnnotating && !showCropper && <Button className="wa-edit-btn" icon="pi pi-pencil" onClick={startAnnotating} aria-label="Annotate image" />}

          <button className="wa-close-all" onClick={handleCloseAll} aria-label="Close attachments">
            ✕
          </button>
        </div>

        {/* caption / toolbar row - hide during crop */}
        {!showCropper && (
          <div className="wa-caption-row">
            {isAnnotating && isImage ? (
              <div className="wa-annotator-toolbar inline">
                <div className="wa-annotator-colors">
                  {COLORS.map((c) => (
                    <button key={c} className={`wa-color-swatch ${brushColor === c ? 'active' : ''}`} style={{ backgroundColor: c }} onClick={() => setBrushColor(c)} aria-label={`Set brush color ${c}`} />
                  ))}
                </div>

                <div className="wa-annotator-size">
                  <span className="wa-size-label">Brush</span>
                  <input type="range" min="2" max="16" value={brushSize} onChange={(e) => setBrushSize(Number(e.target.value))} />
                  <span className="wa-size-value">{brushSize}px</span>
                </div>

                <div className="wa-annotator-actions">
                  <Button className="annotations-cancel-btn" onClick={handleCancelAnnotate}>
                    Cancel
                  </Button>
                  <Button className="annotations-apply-btn" onClick={handleApplyAnnotation} disabled={isUploadingImage}>
                    Apply
                  </Button>
                </div>
              </div>
            ) : (
              <textarea className="wa-caption-input" placeholder="Add a caption..." rows={2} value={getCaption(active?.id)} onChange={(e) => updateCaptionLocal(active.id, e.target.value)} />
            )}
          </div>
        )}
      </div>

      {/* bottom thumbnail strip with add + thumbnails + send on right */}
      <div className="wa-thumb-bottom">
        <input type="file" ref={fileInputRef} style={{ display: 'none' }} multiple onChange={handleFileSelect} />

        <div className="wa-thumb-left">
          <div className="wa-thumbs" role="list">
            {attachments.map((att, idx) => {
              const thumbIsImage = att.mime?.startsWith?.('image/')
              const thumbIsVideo = att.mime?.startsWith?.('video/')

              return (
                <div key={att.id} className={`wa-thumb-item ${idx === activeIndex ? 'active' : ''}`} onClick={() => setActiveIndex(idx)} title={att.name} role="listitem">
                  <div className="wa-thumb-media">{renderThumb(att, thumbIsImage, thumbIsVideo)}</div>
                  <button
                    className="wa-thumb-remove"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRemoveAttachment(att.id)
                    }}
                    aria-label={`Remove ${att.name}`}
                    title="Remove"
                  >
                    ✕
                  </button>
                </div>
              )
            })}
          </div>

          <button type="button" className="wa-thumb-add" onClick={() => fileInputRef.current.click()} title="Add more" aria-label="Add attachments">
            +
          </button>
        </div>

        <div className="wa-thumb-send">
          <Button rounded icon={isUploadingImage ? 'pi pi-spin pi-spinner' : 'pi pi-send send-icon'} className="send-button" onClick={handleSend} disabled={isUploadingImage} />
        </div>
      </div>
    </div>
  )
}
